/*
 * useInfoCard
 *
 * Purpose:
 * - Keep MainComponent lean by owning the single info-card state shared by the
 *   vessel card and the waypoint card, plus the setters and auto-close effects.
 *
 * Card shape: { type: "vessel" | "waypoint", data, anchor, ... }. Only one card
 * is open at a time, so both card types live in one `activeCard` state.
 *
 * Responsibilities:
 * - Vessel-card setters (setSelectedVessel / setSelectedVesselAnchor) and the
 *   derived selectedVessel / selectedVesselAnchor.
 * - Waypoint-card setter (openWaypointCard).
 * - closeCard: close whichever card is open.
 * - Draft (Pathfinder) waypoint cards follow their point live: re-resolved by
 *   position on every store change, closed once the point is gone (moved,
 *   deleted, draft discarded).
 * - Waypoint cards of editable drafts carry an `edit` object (delete, leg type).
 * - Leg-alert cards carry `leg` ({ toLabel, toCoords }) for the "WP n → m" subtitle.
 * - Auto-close the vessel card when its vessel is no longer marked (Pathfinder
 *   excepted — it never appears in markedVesselNames).
 * - Close the card on a user-initiated map drag/zoom (delegated to
 *   useCloseCardOnUserMove) when a Leaflet `map` is provided.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import useCloseCardOnUserMove from "@/features/map/useCloseCardOnUserMove.jsx";
import { useRouteStore } from "@/features/route/state/RouteStore.jsx";
import { selectRoutePointsGeojson } from "@/features/route/state/RouteSelectors.jsx";
import { legtypeOf, sameLonLat } from "@/features/voyagePlanner/manualSketch";

// Start / WP n / Finish among a draft's user-set points.
const draftPointLabel = (originals, index) =>
  index === 0
    ? "Start"
    : index === originals.length - 1
      ? "Finish"
      : `WP ${originals[index]?.properties?.number ?? index + 1}`;

export default function useInfoCard({ hasCandidateRoute, markedVesselNames, map }) {
  // Unified card state: { type: "vessel"|"waypoint", data, anchor, ... }
  const [activeCard, setActiveCard] = useState(null);
  const { state: routeState } = useRouteStore();

  // Backwards-compatible setters for the vessel card (used by VesselController).
  const setSelectedVessel = useCallback((vessel) => {
    if (!vessel) {
      setActiveCard((prev) => (prev?.type === "vessel" ? null : prev));
      return;
    }
    setActiveCard((prev) => ({
      type: "vessel",
      data: vessel,
      anchor: prev?.type === "vessel" ? prev.anchor : null,
    }));
  }, []);

  const setSelectedVesselAnchor = useCallback((anchor) => {
    setActiveCard((prev) => {
      if (!prev || prev.type !== "vessel") return prev;
      return { ...prev, anchor };
    });
  }, []);

  // Derived for backwards compat
  const selectedVessel = activeCard?.type === "vessel" ? activeCard.data : null;
  const selectedVesselAnchor =
    activeCard?.type === "vessel" ? activeCard.anchor : null;

  // Waypoint card setter (used by RoutePoints)
  const openWaypointCard = useCallback(
    (feature, latLng, routeType, vesselName, state, edit = null, leg = null) => {
      setActiveCard({
        type: "waypoint",
        data: feature,
        anchor: latLng,
        routeType,
        vesselName,
        state,
        edit,
        leg,
      });
    },
    [],
  );

  const closeCard = useCallback(() => setActiveCard(null), []);

  const waypointRouteId =
    activeCard?.type === "waypoint" && activeCard.routeType === "Pathfinder"
      ? activeCard.data?.properties?.routeId ?? null
      : null;
  const liveWaypoint = useMemo(() => {
    if (waypointRouteId == null) return null;
    const features =
      selectRoutePointsGeojson(routeState, waypointRouteId)?.features ?? [];
    const coords = activeCard?.data?.geometry?.coordinates;
    // Calculated points have no edit actions: only close once they are gone.
    if (activeCard?.data?.properties?.type !== "original_point") {
      return features.some((f) => sameLonLat(f?.geometry?.coordinates, coords))
        ? null
        : { gone: true };
    }
    const originals = features.filter(
      (f) => f?.properties?.type === "original_point",
    );
    const index = originals.findIndex((f) =>
      sameLonLat(f?.geometry?.coordinates, coords),
    );
    if (index < 0) return { gone: true };
    const toCoords = activeCard?.leg?.toCoords;
    const toIndex = toCoords
      ? originals.findIndex((f) => sameLonLat(f?.geometry?.coordinates, toCoords))
      : -1;
    return {
      feature: originals[index],
      label: draftPointLabel(originals, index),
      nextLabel:
        index < originals.length - 1
          ? draftPointLabel(originals, index + 1)
          : null,
      toLabel: toIndex >= 0 ? draftPointLabel(originals, toIndex) : null,
    };
  }, [waypointRouteId, routeState, activeCard?.data, activeCard?.leg]);

  useEffect(() => {
    if (liveWaypoint?.gone) setActiveCard(null);
  }, [liveWaypoint]);

  // The card renders the live point; the edit actions read it as well.
  const renderedCard = useMemo(() => {
    if (!liveWaypoint?.feature) return activeCard;
    const { feature, label, nextLabel, toLabel } = liveWaypoint;
    return {
      ...activeCard,
      data: feature,
      label,
      leg: activeCard.leg && toLabel ? { ...activeCard.leg, toLabel } : activeCard.leg,
      edit: activeCard.edit
        ? {
            ...activeCard.edit,
            legtype: legtypeOf(feature),
            nextLabel,
            // The finish has no outgoing leg.
            onLegtypeChange: nextLabel
              ? activeCard.edit.onLegtypeChange
              : null,
          }
        : null,
    };
  }, [activeCard, liveWaypoint]);

  // Close Pathfinder waypoint card when the suggested route is saved/discarded
  useEffect(() => {
    if (!hasCandidateRoute) {
      setActiveCard((prev) =>
        prev?.type === "waypoint" && prev?.routeType === "Pathfinder"
          ? null
          : prev,
      );
    }
  }, [hasCandidateRoute]);

  // Close the vessel card when its vessel is no longer marked.
  useEffect(() => {
    if (!selectedVessel?.vesselName) return;
    // Pathfinder is a pseudo-vessel that never appears in markedVesselNames —
    // don't auto-close its card.
    if (selectedVessel?.routeType === "Pathfinder") return;
    const isStillSelected = (markedVesselNames ?? []).some(
      (entry) => (entry?.vesselName ?? "") === selectedVessel.vesselName,
    );
    if (!isStillSelected) {
      setSelectedVessel(null);
      setSelectedVesselAnchor(null);
    }
  }, [markedVesselNames, selectedVessel, setSelectedVessel, setSelectedVesselAnchor]);

  useCloseCardOnUserMove({ map, closeCard });

  return {
    activeCard: renderedCard,
    closeCard,
    setSelectedVessel,
    setSelectedVesselAnchor,
    selectedVessel,
    selectedVesselAnchor,
    openWaypointCard,
  };
}
